import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";
import { Building2, TrendingUp, TrendingDown, DollarSign, Calendar, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

const COLORS = [
  "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", 
  "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16"
];

export default function PropertyComparison() {
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState("bar");

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedProperties.length > 0) {
      fetchComparisonData();
    } else {
      setComparisonData([]);
    }
  }, [selectedProperties]);

  const fetchInitialData = async () => {
    try {
      const [propsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/properties`),
        axios.get(`${API}/dashboard/summary`)
      ]);
      
      setProperties(propsRes.data);
      
      if (summaryRes.data.monthly_data) {
        const months = summaryRes.data.monthly_data.map(m => ({
          value: m.month,
          label: new Date(m.month + '-01').toLocaleString('en-US', { month: 'short', year: 'numeric' })
        }));
        setAvailableMonths(months);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const fetchComparisonData = async () => {
    try {
      // Fetch stats for all months for selected properties
      const allMonthsData = [];
      
      for (const month of availableMonths) {
        const response = await axios.get(`${API}/properties/stats/all?period=${month.value}`);
        const monthStats = response.data.filter(s => 
          selectedProperties.includes(s.property_id)
        );
        
        const monthEntry = {
          month: month.label,
          monthKey: month.value
        };
        
        // Use property_id as key to avoid collisions
        monthStats.forEach(stat => {
          monthEntry[`${stat.property_id}_income`] = stat.monthly_income;
          monthEntry[`${stat.property_id}_expense`] = stat.monthly_expense;
          monthEntry[`${stat.property_id}_net`] = stat.monthly_net;
        });
        
        allMonthsData.push(monthEntry);
      }
      
      // Data is already in chronological order from API, no need to reverse
      setComparisonData(allMonthsData);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      toast.error("Failed to load comparison data");
    }
  };

  const toggleProperty = (propertyId) => {
    setSelectedProperties(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      }
      if (prev.length >= 5) {
        toast.warning("Maximum 5 properties can be compared at once");
        return prev;
      }
      return [...prev, propertyId];
    });
  };

  const getPropertyDisplayName = (propId) => {
    const prop = properties.find(p => p.id === propId);
    if (!prop) return 'Unknown';
    return prop.name.length > 20 ? prop.name.substring(0, 20) + '...' : prop.name;
  };

  const getPropertyFullName = (propId) => {
    const prop = properties.find(p => p.id === propId);
    return prop?.name || 'Unknown';
  };

  const calculateTotals = () => {
    const totals = {};
    
    selectedProperties.forEach(propId => {
      totals[propId] = {
        income: 0,
        expense: 0,
        net: 0
      };
      
      comparisonData.forEach(month => {
        totals[propId].income += month[`${propId}_income`] || 0;
        totals[propId].expense += month[`${propId}_expense`] || 0;
        totals[propId].net += month[`${propId}_net`] || 0;
      });
    });
    
    return totals;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="comparison-loading">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-in" data-testid="property-comparison-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-primary" data-testid="comparison-title">
          Property Comparison
        </h1>
        <p className="text-base text-muted-foreground mt-2">Compare performance across your properties month-over-month</p>
      </div>

      {/* Property Selection */}
      <Card className="bg-white rounded-xl border border-border/60 shadow-sm mb-6" data-testid="property-selection-card">
        <CardHeader className="p-6 pb-4">
          <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Select Properties to Compare (Max 5)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {properties.length === 0 ? (
            <p className="text-muted-foreground">No properties available. Add properties first.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {properties.map((property, index) => {
                const isSelected = selectedProperties.includes(property.id);
                return (
                  <div
                    key={property.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-muted/30'
                    }`}
                    onClick={() => toggleProperty(property.id)}
                    data-testid={`select-property-${property.id}`}
                  >
                    <Checkbox 
                      checked={isSelected}
                      className="pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{property.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{property.address}</p>
                    </div>
                    {isSelected && (
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[selectedProperties.indexOf(property.id) % COLORS.length] }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {selectedProperties.length > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Selected:</span>
              {selectedProperties.map((propId, index) => {
                const prop = properties.find(p => p.id === propId);
                return (
                  <Badge 
                    key={propId}
                    className="text-white"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    data-testid={`selected-badge-${propId}`}
                  >
                    {prop?.name}
                  </Badge>
                );
              })}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedProperties([])}
                className="text-muted-foreground hover:text-foreground"
                data-testid="clear-selection-btn"
              >
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedProperties.length === 0 ? (
        <Card className="bg-white rounded-xl border border-border/60 shadow-sm p-12 text-center">
          <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Select Properties to Compare</h3>
          <p className="text-muted-foreground">Select properties above to see the comparison</p>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            {selectedProperties.map((propId, index) => {
              const propTotals = totals[propId] || { income: 0, expense: 0, net: 0 };
              const displayName = getPropertyDisplayName(propId);
              const fullName = getPropertyFullName(propId);
              return (
                <Card 
                  key={propId}
                  className="bg-white rounded-xl border-2 shadow-sm"
                  style={{ borderColor: COLORS[index % COLORS.length] }}
                  data-testid={`comparison-summary-${index}`}
                >
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-sm font-medium truncate" title={fullName}>
                      {displayName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-600" /> Income
                      </span>
                      <span className="text-sm font-semibold text-emerald-700">
                        {formatCurrency(propTotals.income)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingDown className="w-3 h-3 text-rose-600" /> Expense
                      </span>
                      <span className="text-sm font-semibold text-rose-700">
                        {formatCurrency(propTotals.expense)}
                      </span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> Net
                        </span>
                        <span className={`text-lg font-bold ${propTotals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {formatCurrency(propTotals.net)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Chart Type Selector */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Month-over-Month Comparison
            </h2>
            <Select value={chartType} onValueChange={setChartType}>
              <SelectTrigger className="w-[150px]" data-testid="chart-type-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bar">Bar Chart</SelectItem>
                <SelectItem value="line">Line Chart</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Net Income Comparison Chart */}
          <Card className="bg-white rounded-xl border border-border/60 shadow-sm mb-6" data-testid="net-income-chart">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="text-lg font-semibold text-primary">Net Income Comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {comparisonData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  {chartType === "bar" ? (
                    <BarChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" stroke="#78716C" fontSize={12} />
                      <YAxis stroke="#78716C" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E7E5E4",
                          borderRadius: "0.75rem",
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      {selectedProperties.map((propId, index) => (
                        <Bar 
                          key={propId}
                          dataKey={`${propId}_net`}
                          fill={COLORS[index % COLORS.length]}
                          name={getPropertyDisplayName(propId)}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  ) : (
                    <LineChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" stroke="#78716C" fontSize={12} />
                      <YAxis stroke="#78716C" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E7E5E4",
                          borderRadius: "0.75rem",
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      {selectedProperties.map((propId, index) => (
                        <Line 
                          key={propId}
                          type="monotone"
                          dataKey={`${propId}_net`}
                          stroke={COLORS[index % COLORS.length]}
                          name={getPropertyDisplayName(propId)}
                          strokeWidth={3}
                          dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 4 }}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No data available for selected properties
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Breakdown Table */}
          <Card className="bg-white rounded-xl border border-border/60 shadow-sm" data-testid="monthly-breakdown-table">
            <CardHeader className="p-6 pb-4">
              <CardTitle className="text-lg font-semibold text-primary">Detailed Monthly Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Month</th>
                      {selectedProperties.map((propId, index) => (
                        <th 
                          key={propId} 
                          className="text-right py-3 px-4 text-sm font-semibold" 
                          style={{ color: COLORS[index % COLORS.length] }}
                          title={getPropertyFullName(propId)}
                        >
                          {getPropertyDisplayName(propId)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((month) => (
                      <tr key={month.monthKey} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-4 text-sm font-medium">{month.month}</td>
                        {selectedProperties.map((propId) => {
                          const net = month[`${propId}_net`] || 0;
                          return (
                            <td 
                              key={propId} 
                              className={`py-3 px-4 text-sm text-right font-semibold ${net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                            >
                              {formatCurrency(net)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-muted/50 font-bold">
                      <td className="py-3 px-4 text-sm">TOTAL</td>
                      {selectedProperties.map((propId) => {
                        const propTotals = totals[propId] || { net: 0 };
                        return (
                          <td 
                            key={propId}
                            className={`py-3 px-4 text-sm text-right ${propTotals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                          >
                            {formatCurrency(propTotals.net)}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
